/* eslint-disable react-hooks/exhaustive-deps */
import {
  GoogleOutlined,
  LockFilled,
  PhoneFilled,
  RightOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import * as msal from '@azure/msal-browser';
import { Button, Form, Input, Select } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import passWord from '../../../assets/icons/password.svg';
import userName from '../../../assets/icons/user_name.svg';
import * as SERVICE from '../../../constants/service.types';
import {
  APP_ID,
  AZURE_APP_ID,
  AZURE_REDIRECT_URI,
  AZURE_USER_NAME,
  GOOGLE_CLIENT_ID,
  GOOGLE_NAMESPACE,
  GOOGLE_USERNAME,
  NOTIFIES,
} from '../../../constants/system.keys';
import {
  getCheckAuthenticationType,
  openMessage,
  socketEmitEvent,
  updateNamespace,
} from '../../../redux/actions';
import { AUTHENTICATION_SERVICE } from '../../../constants/auth';
import { VerifyPasskey } from './LoginByPasskey';
import {
  checkLDAPAccount,
  getChallengeAuthenRegisterWebAuthn,
} from '../../../hooks/socket/auth';
import { FingerprintIcon } from '../../../icons/Fingerprint';
import { VscAzure } from 'react-icons/vsc';
import { beeXLogger } from '../../../helpers/logger';
import './index.scss';

const { Option } = Select;

const HOSTNAME = window.location.hostname;
const LOGIN_FORM_CLASS = {
  'cloudcam1.hoasengroup.vn': {
    input: 'hoasen-input',
    submit: 'hoasen-submit',
    feedback: 'feedback hoasen-feedback',
    namespace: 'hoasen-namespace',
  },
  'cloudcam.hoasenhome.vn': {
    input: 'hoasen-input',
    submit: 'hoasen-submit',
    feedback: 'feedback hoasen-feedback',
    namespace: 'hoasen-namespace',
  },
};

const dedupeNamespaces = (items) => [
  ...new Map(
    items
      .filter((item) => item?.namespace)
      .map((item) => [item.namespace, item]),
  ).values(),
];

const dedupeAuthTypes = (items) => [
  ...new Map(
    items
      .filter((item) => item?.namespace)
      .map((item) => [
        JSON.stringify({ serviceId: item?.serviceId, type: item?.type }),
        item,
      ]),
  ).values(),
];

const LoginByPhoneNumberComponent = ({ onBack }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [formPassword] = Form.useForm();

  const [namespaces, setNamespaces] = useState([]);
  const [selectNamespace, setSelectNamespace] = useState(null);
  const [dataUserName, setDataUserName] = useState('');
  const [step, setStep] = useState(0);
  const [stepPhone, setStepPhone] = useState(0);
  const [authenticationTypes, setAuthenticationTypes] = useState([]);
  const [selectedAuthenticationType, setSelectedAuthenticationType] =
    useState(null);
  const [challengePasskey, setChallengePasskey] = useState(null);
  const [phone, setPhone] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [countDown, setCountDown] = useState(30);
  const [isTimeout, setIsTimeout] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [isLoadingCode, setIsLoadingCode] = useState(false);

  const { t } = useTranslation();
  const refCountDown = useRef();
  const phonenumberRef = useRef(null);
  const passwordRef = useRef(null);
  const codeRef = useRef(null);
  const timeoutRef = useRef(null);

  const {
    authData,
    listNamespaceAccount,
    authenticationAccount,
    listAuthenticationTypeAccount,
  } = useSelector((state) => state);

  const clearRequestTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const setRequestTimeout = useCallback(() => {
    clearRequestTimeout();
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setIsLoadingPassword(false);
      setIsLoadingCode(false);
      setIsTimeout(true);
    }, 10000);
  }, [clearRequestTimeout]);

  useEffect(() => () => clearRequestTimeout(), [clearRequestTimeout]);

  useEffect(() => {
    phonenumberRef.current.input.focus();
  }, []);

  useEffect(() => {
    if (step === 2 || step === 4) passwordRef.current.input.focus();
  }, [step]);

  useEffect(() => {
    if (step === 3) codeRef.current.input.focus();
  }, [step]);

  useEffect(() => {
    if (countDown === 0) clearInterval(refCountDown.current);
  }, [countDown]);

  useEffect(() => {
    if (step !== 0) return;
    setChallengePasskey(null);
    setAuthenticationTypes([]);
    form.resetFields();
    clearRequestTimeout();
    setIsLoading(false);
    setIsLoadingPassword(false);
    setIsLoadingCode(false);
  }, [step, clearRequestTimeout]);

  useEffect(() => {
    if (!authenticationAccount) return;
    clearRequestTimeout();
    setIsLoadingPassword(false);
    if (authenticationAccount.result < 0) {
      formPassword.setFields([
        { name: 'password', errors: [t('Password is incorrect!')] },
      ]);
    }
  }, [authenticationAccount]);

  useEffect(() => {
    if (!authData) return;
    clearRequestTimeout();
    setIsLoadingCode(false);
    if (authData.result !== 1) {
      form.setFields([{ name: 'code', errors: [authData.message] }]);
    }
    return () => form.resetFields();
  }, [authData]);

  useEffect(() => {
    if (!listAuthenticationTypeAccount) return;
    if (listAuthenticationTypeAccount.requestId !== requestId) return;

    clearRequestTimeout();
    setIsLoading(false);

    beeXLogger.debug(
      'CHECK AUTHENTICATION TYPE: ',
      listAuthenticationTypeAccount,
    );

    if (listAuthenticationTypeAccount.result !== 1) {
      form.setFields([
        { name: 'userName', errors: [t('The account does not exist!')] },
      ]);
      return;
    }

    const data = JSON.parse(listAuthenticationTypeAccount.data || '[]');
    if (!data?.length) return;

    beeXLogger.debug('CHECK AUTHENTICATION TYPE data: ', data);
    setAuthenticationTypes(data);
    setNamespaces(dedupeNamespaces(data));
    setStep(5);
  }, [listAuthenticationTypeAccount, requestId]);

  useEffect(() => {
    if (step !== 5 || !authenticationTypes.length) return;
    const unique = dedupeAuthTypes(authenticationTypes);
    if (unique.length === 1) {
      handleConfirmAuthenticationType(unique[0].serviceId, unique[0].type);
    }
  }, [step, authenticationTypes]);

  const goBack = useCallback(
    (resetForm = form) => {
      resetForm.resetFields();
      if (authenticationTypes.length === 1) {
        dispatch(getCheckAuthenticationType(null));
        setAuthenticationTypes([]);
        setStep(0);
      } else {
        setStep(5);
      }
    },
    [authenticationTypes.length, dispatch],
  );

  const applyNamespace = useCallback(
    (ns) => {
      if (!ns) return;
      setSelectNamespace(ns);
      dispatch(updateNamespace(ns));
    },
    [dispatch],
  );

  const sendActivationCode = useCallback(
    (phonenumber) => {
      if (!phonenumber?.length) return;
      if (countDown > 0) {
        refCountDown.current = setInterval(
          () => setCountDown((prev) => prev - 1),
          1000,
        );
      }
      dispatch(
        socketEmitEvent({
          appServiceType: SERVICE.GET_ACTIVATION_CODE,
          requestId: Date.now(),
          body: JSON.stringify({ phonenumber, appId: APP_ID }),
        }),
      );
    },
    [countDown, dispatch],
  );

  const handleResend = useCallback(() => {
    if (!phone) return;
    setCountDown(30);
    clearInterval(refCountDown.current);
    sendActivationCode(phone);
  }, [phone, sendActivationCode]);

  const handleLogin = useCallback(
    (values) => {
      const time = Date.now();
      setRequestId(time);
      setDataUserName(values.userName || '');
      setIsLoading(true);
      setRequestTimeout();
      dispatch(
        socketEmitEvent({
          appServiceType: SERVICE.CHECK_AUTHENTICATION_TYPE,
          requestId: time,
          body: JSON.stringify({
            username: values.userName || '',
            appId: APP_ID,
          }),
        }),
      );

      checkLDAPAccount({ username: values.userName || '', appId: APP_ID }).then(
        async (res) => {
          if (!Array.isArray(res) || !res.length) return;
          for (const item of res) {
            if (!item?.credentialId) return;
            const challenge = await getChallengeAuthenRegisterWebAuthn({
              appId: APP_ID,
              phonenumber: values.userName,
            });
            setChallengePasskey(challenge);
            return;
          }
        },
      );
    },
    [dispatch, setRequestTimeout],
  );

  const handleLoginPhone = useCallback(
    (values) => {
      if (!values.code?.length) return;
      dispatch(
        socketEmitEvent({
          appServiceType: SERVICE.AUTHENTICATION,
          requestId: Date.now(),
          body: JSON.stringify({
            activationCode: values.code,
            appId: APP_ID,
            namespace: selectNamespace,
            encrypted: 1,
            agent: `${window.navigator.userAgent}_web`,
          }),
        }),
      );
      setIsLoadingCode(true);
      setRequestTimeout();
    },
    [dispatch, selectNamespace, setRequestTimeout],
  );

  const handlePasswordLDAP = useCallback(
    (values) => {
      const accountLdapId =
        namespaces.find((item) => item?.namespace === selectNamespace)
          ?.accountLdapId ?? '';
      dispatch(
        socketEmitEvent({
          appServiceType: SERVICE.AUTHENTICATION_LDAP,
          requestId: Date.now(),
          body: JSON.stringify({
            appId: APP_ID,
            accountLdapId,
            password: values.password,
            username: dataUserName,
            namespace: selectNamespace,
            language: localStorage.getItem('language') ?? 'vi',
          }),
        }),
      );
      setIsLoadingPassword(true);
      setRequestTimeout();
    },
    [dispatch, namespaces, selectNamespace, dataUserName, setRequestTimeout],
  );

  const handlePassword = useCallback(
    (values) => {
      beeXLogger.debug('handlePassword');
      dispatch(
        socketEmitEvent({
          appServiceType: SERVICE.AUTHENTICATION_PASSWORD,
          requestId: Date.now(),
          body: JSON.stringify({
            appId: APP_ID,
            password: values.password,
            username: dataUserName,
            namespace: selectNamespace,
            language: localStorage.getItem('language') ?? 'vi',
          }),
        }),
      );
      setIsLoadingPassword(true);
      setRequestTimeout();
    },
    [dispatch, dataUserName, selectNamespace, setRequestTimeout],
  );

  const handleLoginWithAzure = useCallback(
    (loginResponseData) => {
      if (!loginResponseData) return;
      const { tenantId, applicationId, namespace } = loginResponseData;

      const msalInstance = new msal.PublicClientApplication({
        auth: {
          clientId: applicationId,
          authority: `https://login.microsoftonline.com/${tenantId}/`,
          redirectUri: AZURE_REDIRECT_URI,
        },
      });

      msalInstance
        .loginPopup({
          scopes: ['openid', 'profile', 'email', 'User.Read'],
          account:
            msalInstance.getAccountByUsername(dataUserName || '') || undefined,
        })
        .then((azureResponse) => {
          dispatch(updateNamespace(namespace));
          localStorage.setItem(AZURE_USER_NAME, dataUserName);
          localStorage.setItem(AZURE_APP_ID, applicationId);
          dispatch(
            socketEmitEvent({
              appServiceType: SERVICE.AUTHENTICATION_AZURE,
              requestId: Date.now(),
              body: JSON.stringify({
                appId: APP_ID,
                ...azureResponse.account,
                idTokenClaims: azureResponse.account?.idTokenClaims,
                namespace,
                language: localStorage.getItem('language') ?? 'vi',
              }),
            }),
          );
        })
        .catch((err) => openMessage(NOTIFIES.WARNING, err.message));
    },
    [dataUserName, dispatch],
  );

  const handleLoginWithGoogle = useCallback(
    (loginResponseData) => {
      beeXLogger.debug('handleLoginWithGoogle: ', loginResponseData);
      if (!loginResponseData) return;
      const { clientId, namespace } = loginResponseData;
      localStorage.setItem(GOOGLE_CLIENT_ID, clientId);
      localStorage.setItem(GOOGLE_NAMESPACE, namespace);
      localStorage.setItem(GOOGLE_USERNAME, dataUserName);
      window.open(
        `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${window.location.origin}/login&&response_type=code&scope=email profile`,
        '_self',
      );
    },
    [dataUserName],
  );

  const handleConfirmAuthenticationType = useCallback(
    (serviceId, type, ns) => {
      beeXLogger.debug('handleConfirmAuthenticationType', {
        serviceId,
        type,
        ns,
      });

      const typeData = authenticationTypes.find(
        (item) =>
          item?.serviceId === serviceId ||
          (item?.type === 'phone' && type === 'phone' && item?.serviceId === 4),
      );

      beeXLogger.debug('typeData: ', typeData);
      setSelectedAuthenticationType({ serviceId, type });

      const isLdap =
        typeData?.serviceId === AUTHENTICATION_SERVICE.LDAP_INTEGRATION ||
        typeData?.type === 'ldap';
      const isPhone =
        typeData?.serviceId === AUTHENTICATION_SERVICE.PHONE_AUTHENTICATION ||
        typeData?.type === 'phone';
      const isPassword =
        typeData?.serviceId ===
        AUTHENTICATION_SERVICE.BASIC_AUTHENTICATION_INTEGRATION;
      const isAzure =
        typeData?.serviceId === AUTHENTICATION_SERVICE.AZURE_AD_INTEGRATION;
      const isGoogle =
        typeData?.serviceId === AUTHENTICATION_SERVICE.GOOGLE_INTEGRATION;
      const isPasskey =
        typeData?.serviceId === AUTHENTICATION_SERVICE.PASSKEY_AUTHENTICATION;

      if (isLdap || isPhone || isPassword) {
        const dedupedNs = dedupeNamespaces(authenticationTypes);

        if (ns) {
          applyNamespace(ns);
        } else if (dedupedNs.length > 1) {
          setSelectNamespace(null);
          setNamespaces(dedupedNs);
          setStep(1);
          return;
        } else {
          applyNamespace(dedupedNs[0]?.namespace || typeData?.namespace || '');
        }

        if (isLdap) {
          setStep(2);
        } else if (isPhone) {
          beeXLogger.debug('Login by Phone');
          setStep(3);
          setStepPhone(1);
          sendActivationCode(dataUserName);
          setPhone(dataUserName);
        } else {
          setStep(4);
        }
      } else if (isAzure) {
        handleLoginWithAzure(typeData);
      } else if (isGoogle) {
        handleLoginWithGoogle(typeData);
      } else if (isPasskey) {
        setStep(6);
      }
    },
    [
      authenticationTypes,
      dataUserName,
      applyNamespace,
      sendActivationCode,
      handleLoginWithAzure,
      handleLoginWithGoogle,
    ],
  );

  const authenticationTypeButtons = useMemo(
    () => [
      {
        label: 'Password',
        icon: <LockFilled />,
        serviceId: AUTHENTICATION_SERVICE.BASIC_AUTHENTICATION_INTEGRATION,
        hidden: !authenticationTypes.some(
          (i) =>
            i?.serviceId ===
            AUTHENTICATION_SERVICE.BASIC_AUTHENTICATION_INTEGRATION,
        ),
        type: 'password',
      },
      {
        label: 'Passkey',
        icon: <FingerprintIcon />,
        serviceId: AUTHENTICATION_SERVICE.PASSKEY_AUTHENTICATION,
        hidden:
          !challengePasskey ||
          !authenticationTypes.some(
            (i) =>
              i?.serviceId === AUTHENTICATION_SERVICE.PASSKEY_AUTHENTICATION,
          ),
        type: 'passkey',
      },
      {
        label: 'Phone number',
        icon: <PhoneFilled />,
        serviceId: AUTHENTICATION_SERVICE.PHONE_AUTHENTICATION,
        hidden: !authenticationTypes.some(
          (i) =>
            i?.type === 'phone' ||
            i?.serviceId === AUTHENTICATION_SERVICE.PHONE_AUTHENTICATION,
        ),
        type: 'phone',
      },
      {
        label: 'Google',
        icon: <GoogleOutlined />,
        serviceId: AUTHENTICATION_SERVICE.GOOGLE_INTEGRATION,
        hidden: !authenticationTypes.some(
          (i) => i?.serviceId === AUTHENTICATION_SERVICE.GOOGLE_INTEGRATION,
        ),
        type: 'google',
      },
      {
        label: 'Azure',
        icon: <VscAzure />,
        serviceId: AUTHENTICATION_SERVICE.AZURE_AD_INTEGRATION,
        hidden: !authenticationTypes.some(
          (i) => i?.serviceId === AUTHENTICATION_SERVICE.AZURE_AD_INTEGRATION,
        ),
        type: 'azure',
      },
    ],
    [authenticationTypes, challengePasskey],
  );

  const cx = (key) => LOGIN_FORM_CLASS[HOSTNAME]?.[key] || '';

  return (
    <>
      {step === 0 && (
        <div className="_2sd-step-login-component">
          <div />
          <Form
            className="form-login"
            form={form}
            size="large"
            name="form_login"
            onFinish={handleLogin}
          >
            <Form.Item
              name="userName"
              rules={[
                { required: true, message: t('Please enter user name!') },
              ]}
            >
              <Input
                ref={phonenumberRef}
                prefix={
                  <img
                    src={userName}
                    alt="user-name"
                    className="site-form-item-icon"
                  />
                }
                placeholder={t('Enter user name')}
                className={cx('input') || (isTimeout ? 'input-timeout' : '')}
                style={isTimeout ? { borderColor: '#a61d24' } : {}}
                onChange={() => isTimeout && setIsTimeout(false)}
              />
            </Form.Item>

            <div
              className={`timeout-message ${isTimeout ? 'timeout-message-visible' : ''}`}
            >
              <div role="alert" style={{ color: '#a61d24' }}>
                {t('Timeout, please try again.')}
              </div>
            </div>

            <div className="confirm-btn">
              <Button
                shape="round"
                onClick={() => {
                  form.resetFields();
                  onBack();
                }}
              >
                {t('Back')}
              </Button>
              <Form.Item shouldUpdate>
                {() => (
                  <Button
                    shape="round"
                    htmlType="submit"
                    type="primary"
                    className={cx('submit')}
                    disabled={
                      !form.isFieldsTouched(true) ||
                      !!form
                        .getFieldsError()
                        .filter(({ errors }) => errors.length).length
                    }
                    loading={isLoading}
                  >
                    {t('Continue')}
                  </Button>
                )}
              </Form.Item>
            </div>
          </Form>
          <div />
        </div>
      )}

      {step === 1 && (
        <div className="_3th-step-login-component">
          <div className="select-namespaces">
            <div />
            <Select
              className={cx('namespace')}
              style={{ width: '100%' }}
              loading={!namespaces}
              onSelect={(value) => setSelectNamespace(value)}
              size="large"
              placeholder={t('Select namespace')}
            >
              {namespaces.map((ns, idx) => (
                <Option key={idx} value={ns.namespace}>
                  {ns.namespace}
                </Option>
              ))}
            </Select>
            <div />
          </div>

          <div className="confirm-btn">
            <Button
              shape="round"
              className="button-size-select"
              onClick={() => {
                setSelectNamespace(null);
                form.resetFields();
                setStep(0);
              }}
            >
              {t('Back')}
            </Button>
            <Button
              shape="round"
              type="primary"
              disabled={!selectNamespace}
              className={cx('submit') || 'button-size-select'}
              onClick={() => {
                if (selectedAuthenticationType) {
                  handleConfirmAuthenticationType(
                    selectedAuthenticationType.serviceId,
                    selectedAuthenticationType.type,
                    selectNamespace, // pass resolved namespace directly
                  );
                }
              }}
            >
              {t('Continue')}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="_2sd-step-login-component">
          <div />
          <Form
            className="form-login"
            form={formPassword}
            size="large"
            name="form_login_password"
            onFinish={handlePasswordLDAP}
          >
            <Form.Item
              name="password"
              rules={[{ required: true, message: t('Please enter password!') }]}
            >
              <Input.Password
                ref={passwordRef}
                prefix={
                  <img
                    src={passWord}
                    alt="password"
                    className="site-form-item-icon"
                  />
                }
                className={cx('input')}
                placeholder={t('Enter password')}
              />
            </Form.Item>
            <div className="confirm-btn">
              <Button shape="round" onClick={() => goBack(formPassword)}>
                {t('Back')}
              </Button>
              <Form.Item shouldUpdate>
                {() => (
                  <Button
                    shape="round"
                    htmlType="submit"
                    type="primary"
                    className={cx('submit')}
                    disabled={
                      !formPassword.isFieldsTouched(true) ||
                      !!formPassword
                        .getFieldsError()
                        .filter(({ errors }) => errors.length).length
                    }
                    loading={isLoadingPassword}
                  >
                    {t('Continue')}
                  </Button>
                )}
              </Form.Item>
            </div>
          </Form>
          <div />
        </div>
      )}

      {step === 3 && (
        <div className="_2sd-step-login-component">
          <div />
          <Form
            className="form-login"
            form={form}
            size="large"
            name="form_login"
            onFinish={handleLoginPhone}
          >
            <Form.Item
              name="code"
              rules={[
                {
                  required: true,
                  message: t('Please enter the verification code'),
                },
              ]}
            >
              <Input
                ref={codeRef}
                prefix={
                  <SolutionOutlined
                    className="site-form-item-icon"
                    style={{ color: '#0e9347' }}
                  />
                }
                placeholder={t('Verification codes')}
                className={cx('input')}
              />
            </Form.Item>

            {stepPhone === 1 && (
              <div
                className={cx('feedback') || 'feedback'}
                style={{ color: '#0e9347' }}
              >
                {countDown > 0 ? (
                  t('VERIFICATION_CODE_TIMER', { timer: countDown })
                ) : (
                  <span onClick={handleResend}>
                    {t("Don't receive verification code, resend again")}
                  </span>
                )}
              </div>
            )}

            <div className="confirm-btn">
              <Button shape="round" onClick={() => goBack(form)}>
                {t('Back')}
              </Button>
              <Form.Item shouldUpdate>
                {() => (
                  <Button
                    shape="round"
                    htmlType="submit"
                    type="primary"
                    className={cx('submit')}
                    disabled={
                      !form.isFieldsTouched(true) ||
                      !!form
                        .getFieldsError()
                        .filter(({ errors }) => errors.length).length
                    }
                    loading={isLoadingCode}
                  >
                    {t('Continue')}
                  </Button>
                )}
              </Form.Item>
            </div>
          </Form>
          <div />
        </div>
      )}

      {step === 4 && (
        <div className="_2sd-step-login-component">
          <div />
          <Form
            className="form-login"
            form={formPassword}
            size="large"
            name="form_login_password"
            onFinish={handlePassword}
          >
            <Form.Item
              name="password"
              rules={[{ required: true, message: t('Please enter password!') }]}
            >
              <Input.Password
                ref={passwordRef}
                prefix={
                  <img
                    src={passWord}
                    alt="password"
                    className="site-form-item-icon"
                  />
                }
                placeholder={t('Enter password')}
              />
            </Form.Item>
            <div className="confirm-btn">
              <Button shape="round" onClick={() => goBack(formPassword)}>
                {t('Back')}
              </Button>
              <Form.Item shouldUpdate>
                {() => (
                  <Button
                    shape="round"
                    htmlType="submit"
                    type="primary"
                    className={cx('submit')}
                    disabled={
                      !formPassword.isFieldsTouched(true) ||
                      !!formPassword
                        .getFieldsError()
                        .filter(({ errors }) => errors.length).length
                    }
                    loading={isLoadingPassword}
                  >
                    {t('Continue')}
                  </Button>
                )}
              </Form.Item>
            </div>
          </Form>
          <div />
        </div>
      )}

      {step === 5 && (
        <div className="_3th-step-login-component">
          <div className="authentication-types">
            {authenticationTypeButtons.map(
              ({ label, icon, serviceId, hidden, type }) => (
                <Button
                  key={label}
                  variant="ghost"
                  className={`login-button ${hidden ? 'hidden' : ''}`}
                  size="lg"
                  icon={icon}
                  onClick={() =>
                    handleConfirmAuthenticationType(serviceId, type)
                  }
                >
                  <div className="login-label">{label}</div>
                  <RightOutlined className="right-icon" />
                </Button>
              ),
            )}
          </div>
          <div className="confirm-btn">
            <Button
              shape="round"
              className="button-size-select"
              onClick={() => {
                setChallengePasskey(null);
                form.resetFields();
                setStep(0);
              }}
            >
              {t('Back')}
            </Button>
          </div>
        </div>
      )}

      {step === 6 && (
        <VerifyPasskey
          challengePasskey={challengePasskey}
          onBack={() => goBack(form)}
        />
      )}
    </>
  );
};

export default LoginByPhoneNumberComponent;
